import React, { Component } from 'react';
import PropTypes from 'prop-types';
import 'stylesheets/design_system_legacy.scss';
import 'stylesheets/bootstrap_overrides/_media_v2.scss';
import $image_url from 'js/utils/image_url';
import routes from 'sitewide-js/core_pre_webpack/routes';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import DownloadModalPage from './download_modal_page.wjs';

export default class ReaderMessageForm extends Component {
  static propTypes = {
    primaryAuthor: PropTypes.shape({
      displayName: PropTypes.string.isRequired,
      photo: PropTypes.string, //default used when not given
    }).isRequired,
    workId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    attachmentId: PropTypes.number.isRequired,
    reactionPrompt: PropTypes.string,
    readerMessageSource: PropTypes.oneOf([
      'unknown',
      'sutd',
      'swp-toolbar',
      'swp-preview-selector-dropdown',
      'news',
      'news-feed',
      'search-results',
      'history',
      'sidebar',
      'work_strip',
      'profile',
      'swp_mentions_sidebar',
      'swp_related_works_sidebar',
      'swp-bulk-download-modal',
      'swp-related-works-grid',
      'library',
      'coronavirus_page',
      'single-article-page',
    ]).isRequired,
    closeModal: PropTypes.func.isRequired,
    showMessageToast: PropTypes.bool.isRequired,
    setReaderMessageLimitReached: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      readerMessageLimitReached: null,
      value: '',
      sending: false,
    };
  }

  render() {
    const placeholderText =
      'Dear ' +
      this.props.primaryAuthor.displayName +
      ',\nWhat sparked my interest was...';
    return (
      <DownloadModalPage closeModal={this.props.closeModal}>
        <div className="u-p10x">
          {this.renderAuthorRow()}
          {this.props.reactionPrompt && (
            <div className="u-pb6x u-fs14">
              <strong>Note from the author:</strong>
              <p>{this.props.reactionPrompt}</p>
            </div>
          )}
          <div className="u-pb6x">
            <textarea
              className="TextArea u-ph3x u-lineHeight1_5 u-fs14"
              placeholder={placeholderText}
              rows="5"
              style={{ height: '200px', resize: 'none' }}
              value={this.state.value}
              onChange={this.handleChange}
              onKeyDown={this.checkForSubmit}
            />
          </div>
          <div style={{ height: '36px', lineHeight: '36px' }}>
            <button
              className="Button Button--sm Button--blue"
              onClick={this.sendReaderMessage}
            >
              {this.buttonText()}
            </button>
            {this.renderSkip()}
          </div>
        </div>
      </DownloadModalPage>
    );
  }

  handleChange = (event) => {
    this.setState({
      value: event.target.value,
    });
  };

  renderAuthorRow() {
    const author = this.props.primaryAuthor;
    const photo =
      author.photo == null ? $image_url('s65_no_pic.png') : author.photo;
    return (
      <div className="u-pb6x media_v2">
        <div className="u-mr3x media-left media-middle">
          <img className="Avatar--lg Avatar" src={photo}></img>
        </div>
        <div className="u-fw700 u-fs16 u-tcGrayDarkest u-lineHeight1 media-body media-middle">
          Let {this.props.primaryAuthor.displayName} know what sparked your
          interest in downloading this paper
        </div>
      </div>
    );
  }

  checkForSubmit = (event) => {
    if (
      (event.ctrlKey || event.metaKey) &&
      (event.keyCode === 13 || event.keyCode === 10)
    ) {
      this.sendReaderMessage();
    }
  };

  sendReaderMessage = () => {
    if (this.state.sending) return;
    this.setState({ sending: true });

    // close modal and start download
    this.props.closeModal();

    if (this.props.setReaderMessageLimitReached) {
      // We only hit the endpoint to check the limit on page load, so this is required
      // to update the limit on the page after a message is sent.
      // (e.g. in case of multiple downloads in one page load)
      this.props.setReaderMessageLimitReached(true);
    }

    // Don't attempt to send an empty message,
    // This case fails silently
    if (this.state.value === '') return;

    $.ajax({
      type: 'POST',
      url: routes.api_v0_reader_messages_path({
        subdomain_param: 'api',
      }),
      data: {
        work_id: this.props.workId,
        attachment_id: this.props.attachmentId,
        message: this.state.value,
        source: this.props.readerMessageSource,
      },
    })
      .done(
        function (response) {
          // We used to setState({sending: false}) here, but it appears that
          // this component is always unmounted by this point in time due to
          // this.props.closeModal() being called above.

          if (response.message_sent) {
            this.attemptToShowToast(false);
          } else {
            this.attemptToShowToast(true);
          }
        }.bind(this),
      )
      .fail(
        function () {
          this.attemptToShowToast(true);
        }.bind(this),
      );
  };

  renderSkip() {
    const handleSkip = () => {
      this.props.closeModal();
    };

    return (
      <span
        className="u-clickable u-fw400 u-fs14 u-tcGrayDark u-lineHeight1 u-ml6x"
        data-dismiss="modal"
        onClick={handleSkip}
      >
        Skip
      </span>
    );
  }

  buttonText() {
    if (this.state.sending) {
      return <i className="fa fa-spin fa-spinner"></i>;
    } else {
      return 'Send';
    }
  }

  attemptToShowToast(error) {
    if (!this.props.showMessageToast) return;
    if (error) {
      Components.Utils.showToast(
        Components.Toasts.BasicToast,
        { text: 'There was an error in sending your message.' },
        { timeMs: 1500, color: 'Red' },
      );
    } else {
      Components.Utils.showToast(
        Components.Toasts.BasicToast,
        { text: 'Message sent' },
        { timeMs: 1500, color: 'Blue' },
      );
    }
  }
}

export function fetchReaderMessageLimit(workId) {
  return new Promise((resolve, reject) => {
    fetchWithCsrf(
      routes.api_v0_reader_message_limit_index_path({
        id: workId,
        subdomain_param: 'api',
      }),
      {
        method: 'GET',
      },
    )
      .then((response) => response.json())
      .then((data) => {
        resolve(data.reader_message_limit_reached);
      })
      .catch(reject);
  });
}

// CITE-232: returns both reader-message limit and the synchronous CiteRight
// encore-email eligibility flag (used to gate the post-download marketing
// modal AB test).
export function fetchPostDownloadModalState(workId) {
  return new Promise((resolve, reject) => {
    fetchWithCsrf(
      routes.api_v0_post_download_state_index_path({
        id: workId,
        subdomain_param: 'api',
      }),
      {
        method: 'GET',
      },
    )
      .then((response) => response.json())
      .then((data) => {
        resolve({
          readerMessageLimitReached: !!data.reader_message_limit_reached,
          citerightMarketingModalEligible:
            !!data.citeright_marketing_modal_eligible,
        });
      })
      .catch(reject);
  });
}
